/**
 * Telegraph zone geometry — the ONE flat-plane hit-test for every ground
 * telegraph shape (circle / point / cone / line / ring / cross), shared by the
 * authoritative server (`backend/src/world/zone.ts` resolves hits with it) and
 * the client's DRAWING of the same shape (`frontend/src/game/combat/`).
 *
 * Why this is its own leaf (2026-09-24, `telegraph-shape-fairness`): it lived in
 * `sim/boss.ts`, which `@great-cloud/shared/client` deliberately excludes (the
 * boss RESOLVER is server-authoritative, AD-025/AD-122). The client therefore
 * could not reach the line half-width, the cross-arm half-width or the hit-test
 * — and drew every shape as a circle while the server resolved cones, lines,
 * rings and crosses (VFX-018 defect D-2). Geometry is not a resolver: it decides
 * nothing the client is not already told on the wire (`TelegraphShape` carries
 * the kind, facing, arc, length and radii), so exposing it is a presentation
 * boundary decision, not a trust one. Nothing here changes what hits; the
 * server calls exactly the same function it called before (re-exported from
 * `boss.ts` so no import moves).
 *
 * Conventions (unchanged from P7.4): plane x east, z south; `facing` is a
 * heading in radians from `atan2(dx, dz)`, so facing 0 points +z and the unit
 * heading is (sin f, cos f). Every magnitude is a `[P7 sim]` tuning default
 * (AD-072).
 */
/** Cross-arm half-width in metres (the band thickness of each cross arm). `[P7 sim]`. */
export const CROSS_ARM_HALF_WIDTH_M = 1.5;
/** Line half-width in metres (a line is a rectangle: length × 2·half-width). `[P7 sim]`. */
export const LINE_HALF_WIDTH_M = 1.5;
/** Default cone half-angle in degrees when a pattern omits arcDeg. `[P7 sim]`. */
export const DEFAULT_CONE_ARC_DEG = 60;
/** Default ring inner-radius as a fraction of the outer radius when omitted. `[P7 sim]`. */
export const DEFAULT_RING_INNER_FRACTION = 0.6;
/** Smallest signed angle between two headings, in radians (−π, π]. */
export function angleDelta(a, b) {
    let d = a - b;
    while (d > Math.PI)
        d -= 2 * Math.PI;
    while (d < -Math.PI)
        d += 2 * Math.PI;
    return d;
}
/**
 * Whether a point (px, pz) is inside a resolved telegraph zone. Pure geometry —
 * the single hit-test both the server and any client preview use, so they never
 * disagree about who is in the danger zone.
 */
export function pointInZone(zone, px, pz) {
    const dx = px - zone.x;
    const dz = pz - zone.z;
    const dist = Math.hypot(dx, dz);
    switch (zone.shape) {
        case 'point':
        case 'circle':
            return dist <= zone.radius;
        case 'ring':
            return dist <= zone.radius && dist >= zone.innerRadius;
        case 'cone': {
            if (dist > zone.length || dist < 1e-6)
                return dist < 1e-6; // the apex is hit
            const heading = Math.atan2(dx, dz);
            return Math.abs(angleDelta(heading, zone.facing)) <= zone.arc;
        }
        case 'line': {
            // Rectangle from the source along `facing` for `length`, half-width LINE_HALF_WIDTH_M.
            const ux = Math.sin(zone.facing);
            const uz = Math.cos(zone.facing);
            const along = dx * ux + dz * uz; // projection onto the heading
            if (along < 0 || along > zone.length)
                return false;
            const perp = Math.abs(dx * uz - dz * ux); // perpendicular distance
            return perp <= LINE_HALF_WIDTH_M;
        }
        case 'cross': {
            // Two perpendicular bands of half-width CROSS_ARM_HALF_WIDTH_M, length = radius.
            if (dist > zone.radius)
                return false;
            const ux = Math.sin(zone.facing);
            const uz = Math.cos(zone.facing);
            const along = Math.abs(dx * ux + dz * uz);
            const perp = Math.abs(dx * uz - dz * ux);
            return along <= CROSS_ARM_HALF_WIDTH_M || perp <= CROSS_ARM_HALF_WIDTH_M;
        }
        default:
            return dist <= zone.radius;
    }
}
//# sourceMappingURL=telegraph-geometry.js.map